import { useCallback, useEffect, useState } from 'react';
import { ClassStudentRecord } from '../mocks/classStudents';
import {
  classStudentService,
  MemberCandidateRecord,
  SaveClassStudentInput,
} from '../services/classStudent.service';

export const useClassStudents = (classId?: string, trimesterId?: string) => {
  const [students, setStudents] = useState<ClassStudentRecord[]>([]);
  const [memberCandidates, setMemberCandidates] = useState<MemberCandidateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMemberCandidates, setIsLoadingMemberCandidates] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStudents = useCallback(async () => {
    if (!classId) {
      setStudents([]);
      setMemberCandidates([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const studentItems = await classStudentService.listByClass(classId, trimesterId);
      setStudents(studentItems);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar os alunos.');
    } finally {
      setIsLoading(false);
    }
  }, [classId, trimesterId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const loadMemberCandidates = useCallback(async () => {
    if (!classId) {
      setMemberCandidates([]);
      return;
    }

    setIsLoadingMemberCandidates(true);

    try {
      const memberItems = await classStudentService.memberCandidates(classId);
      setMemberCandidates(memberItems);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar os membros.');
    } finally {
      setIsLoadingMemberCandidates(false);
    }
  }, [classId]);

  const createStudent = async (input: SaveClassStudentInput) => {
    if (!classId) {
      throw new Error('Turma nao encontrada.');
    }

    const student = await classStudentService.create(classId, input);
    setStudents((current) => [student, ...current.filter((item) => item.id !== student.id)]);
    await loadStudents();

    return student;
  };

  const updateStudent = async (studentId: string, input: SaveClassStudentInput) => {
    const student = await classStudentService.update(studentId, input);
    setStudents((current) => current.map((item) => (item.id === studentId ? student : item)));

    return student;
  };

  const transferStudent = async (studentId: string, targetClassId: string) => {
    const student = await classStudentService.transfer(studentId, targetClassId);
    setStudents((current) => current.filter((item) => item.id !== studentId));
    await loadStudents();

    return student;
  };

  const removeStudent = async (studentId: string) => {
    await classStudentService.remove(studentId);
    setStudents((current) => current.filter((item) => item.id !== studentId));
    await loadStudents();
  };

  return {
    students,
    memberCandidates,
    isLoading,
    isLoadingMemberCandidates,
    error,
    reloadStudents: loadStudents,
    reloadMemberCandidates: loadMemberCandidates,
    createStudent,
    updateStudent,
    transferStudent,
    removeStudent,
  };
};
